import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Vendor, VendorType } from '@/types/resources/vendor';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  QueryConstraint,
  arrayUnion,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { removeUndefined } from '@/utils/removeUndefined';
import { addWorkspaceAndActivity, createActivityRecord, createDetailedActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '@/types/users';
import { loadMockData } from '@/mock';

// Helper function to filter mock data
function filterMockData(
  data: Vendor[],
  searchTerm?: string,
  status?: string,
  category?: string,
): Vendor[] {
  let filteredData = data;
  if (searchTerm) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(vendor =>
      vendor.name.toLowerCase().includes(lowercasedSearch) ||
      vendor.contactPerson.toLowerCase().includes(lowercasedSearch) ||
      vendor.email?.toLowerCase().includes(lowercasedSearch)
    );
  }
  if (status) {
    filteredData = filteredData.filter(vendor => vendor.status === status);
  }
  if (category) {
    filteredData = filteredData.filter(vendor => vendor.category === category);
  }
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const VENDORS_COLLECTION = 'vendors';

// Types
export interface VendorsQueryParams {
  searchTerm?: string;
  status?: string;
  category?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

export interface CreateVendorInput extends Omit<Vendor, 'id' | 'activityRecords' | 'workspace'> {
  type: VendorType;
  status: Vendor['status'];
}

// Vendor API
export async function getVendors({
  searchTerm,
  status,
  category,
  pageSize,
  page = 1,
  lastDoc,
  sortBy,
  sortOrder,
  firestoreUser
}: VendorsQueryParams): Promise<{ vendors: Vendor[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock vendors with filters', { searchTerm, status, category, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockVendors = await loadMockData<Vendor[]>('vendors', firestoreUser.language);
    const filteredData = filterMockData(mockVendors, searchTerm, status, category);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      vendors: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getVendors', { searchTerm, status, category, pageSize, page, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const vendorsRef = collection(db, VENDORS_COLLECTION);
    const constraints: QueryConstraint[] = [
      where('workspace.id', '==', firestoreUser.workspace.id)
    ];

    // Add search constraints if searchTerm is provided
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff'),
        where('contactPerson', '>=', searchTerm),
        where('contactPerson', '<=', searchTerm + '\uf8ff'),
        where('email', '>=', searchTerm),
        where('email', '<=', searchTerm + '\uf8ff')
      );
    }

    // Add filter constraints
    if (status) {
      constraints.push(where('status', '==', status));
    }
    if (category) {
      constraints.push(where('category', '==', category));
    }

    // Add sorting if specified
    if (sortBy) {
      constraints.push(orderBy(sortBy, sortOrder || 'asc'));
    }

    // Add pagination
    if (pageSize) {
      constraints.push(limit(pageSize));
    }
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    // Build and execute query
    const q = query(vendorsRef, ...constraints);
    const snapshot = await getDocs(q);
    const vendors = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Vendor));

    return {
      vendors,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: snapshot.size // Assuming snapshot.size is the total count for the query
    };
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw new Error('Failed to fetch vendors');
  }
}

export async function getVendorById(id: string, firestoreUser: User): Promise<Vendor | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock vendor with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockVendors = await loadMockData<Vendor[]>('vendors', firestoreUser.language);
    const vendor = mockVendors.find(v => v.id === id) || null;
    return vendor;
  }

  console.log('API Call: getVendorById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the vendor document directly by ID
    const vendorRef = doc(db, VENDORS_COLLECTION, id);
    const vendorDoc = await getDoc(vendorRef);
    
    if (!vendorDoc.exists()) {
      return null;
    }

    const vendorData = vendorDoc.data();
    
    // Check if the vendor belongs to the current workspace
    if (vendorData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...vendorData, id: vendorDoc.id } as Vendor;
  } catch (error) {
    console.error('Error fetching vendor:', error);
    throw new Error('Failed to fetch vendor');
  }
}

export async function addVendor(
  vendor: CreateVendorInput,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addVendor', { vendor, firestoreUser });
  
  try {
    const vendorData = addWorkspaceAndActivity(vendor, firestoreUser, 'created');

    // Create vendor record
    const vendorRef = doc(collection(db, VENDORS_COLLECTION));
    await setDoc(vendorRef, removeUndefined(vendorData));
    
    // Log the created document ID
    console.log('Created vendor with ID:', vendorRef.id);
    
    return vendorRef.id;
  } catch (error) {
    console.error('Error adding vendor:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to add vendor');
  }
}

export async function updateVendor(
  id: string,
  data: Partial<Vendor>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateVendor', { id, data, firestoreUser });
  
  try {
    const docRef = doc(db, VENDORS_COLLECTION, id);
    const vendorDoc = await getDoc(docRef);

    if (!vendorDoc.exists()) {
      throw new Error('Vendor not found');
    }

    const oldVendorData = vendorDoc.data() as Vendor;
    const activityRecord = createDetailedActivityRecord('updated', oldVendorData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update data without activityRecords
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating vendor:', error);
    throw new Error('Failed to update vendor');
  }
}

export async function deleteVendor(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deleteVendor', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the vendor document directly
    const vendorRef = doc(db, VENDORS_COLLECTION, id);
    const vendorDoc = await getDoc(vendorRef);

    if (!vendorDoc.exists()) {
      throw new Error('Vendor not found');
    }

    const vendorData = vendorDoc.data();
    if (vendorData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Vendor not found in current workspace');
    }

    const activityRecord = createActivityRecord('deleted', vendorData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(vendorRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the document
    await deleteDoc(vendorRef);
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw new Error('Failed to delete vendor');
  }
}

// Bulk upload vendors using Firestore batch operations
export async function addVendorsBulk(
  vendors: Omit<Vendor, 'id' | 'activityRecords' | 'workspace'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < vendors.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, vendors.length);
    }
    return { successCount: vendors.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < vendors.length; i += CHUNK_SIZE) {
      chunks.push(vendors.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((vendorData) => {
        const vendorRef = doc(collection(db, VENDORS_COLLECTION));
        const vendorWithMetadata = addWorkspaceAndActivity(vendorData, firestoreUser, 'created');
        const cleanedVendor = removeUndefined(vendorWithMetadata);
        
        batch.set(vendorRef, cleanedVendor);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, vendors.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = vendors.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Error in bulk upload:', error);
    throw new Error('Failed to upload vendors in bulk');
  }
} 